import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { isMobile as isMobileDevice } from 'react-device-detect';
import { useTranslation } from 'react-i18next';

import { LegacyDragIcon, LegacyTrashIcon } from '@pkg/assets/icons/deprecated';
import { useAppSelector } from '@pkg/hooks';
import { EidCategory } from '@pkg/types';

import config from '@app/config';
import { getDisplayUrl, LinkItem, LinkItemType } from '@app/helpers';

import YatLinkForm from '../YatLinkForm/YatLinkForm';
import YatLinkIcon from '../YatLinkIcon/YatLinkIcon';
import YatLinkInput from '../YatLinkInput/YatLinkInput';

import './_YatLink.scss';

interface YatLinkProps {
    editable: boolean;

    onDelete: (linkItem: LinkItem) => void;
    isLastLink?: boolean;
    isDragging: boolean;
    linkItem: LinkItem;
    updateTagsQueue: (eidCategory: EidCategory, data: string, deleteHashes?: string[]) => void;
    onPickerOpened: (open: boolean) => void;
    setIsDirty: (isDirty: boolean) => void;
    emojiId: string;
}

function YatLink({
    editable,
    onDelete,
    isLastLink,
    isDragging,
    linkItem: originalLinkItem,
    updateTagsQueue,
    onPickerOpened,
    setIsDirty,
    emojiId,
}: YatLinkProps) {
    const { t } = useTranslation('yat_page');
    const { features } = useAppSelector((state) => state.featureFlags);
    const [debounce, setDebounce] = useState<number>(0);
    const [linkItem, setLinkItem] = useState<LinkItem>(originalLinkItem);
    const [titleInvalid, setTitleInvalid] = useState<boolean>();
    const [urlInvalid, setUrlInvalid] = useState<boolean>();
    const [urlFocused, setUrlFocused] = useState<boolean>(false);
    const [url, setUrl] = useState(decodeURI(linkItem.url));

    const setErrors = useCallback(() => {
        const linkItemErrors = linkItem.hasError();
        setTitleInvalid(!!linkItemErrors.find((e) => e.field === LinkItemType.title));
        setUrlInvalid(!!linkItemErrors.find((e) => e.field === LinkItemType.url));
    }, [linkItem]);

    useEffect(() => {
        setErrors();
    }, [setErrors]);

    const handleSave = useCallback(() => {
        linkItem.decodedUrl = getDisplayUrl(url).url;
        clearTimeout(debounce);

        if (linkItem.dirty) {
            setDebounce(
                window.setTimeout(() => {
                    const deleteTags = linkItem.hash ? [linkItem.hash] : undefined;
                    updateTagsQueue(EidCategory.DELIMITED_WEB_URL, linkItem.convertToDelimitedLinkTag(), deleteTags);
                }, 5),
            );
        }
    }, [linkItem, updateTagsQueue, setDebounce, debounce, url]);

    const handleUrlChange = (url) => {
        setUrl(url);
    };

    const handleValueChange = useCallback(
        (value, field: LinkItemType) => {
            let triggerSave = false;
            switch (field) {
                case LinkItemType.title:
                    linkItem.title = value;
                    break;
                case LinkItemType.url:
                    linkItem.decodedUrl = value;
                    break;
                case LinkItemType.description:
                    linkItem.description = value;
                    break;
                case LinkItemType.emoji:
                    linkItem.emoji = value;
                    triggerSave = true;
                    clearTimeout(debounce);
                    break;
                default:
                    break;
            }

            if (linkItem.dirty) {
                setIsDirty(true);
                //Object needs to get recreated to trigger a render.
                const newLinkItem = new LinkItem(linkItem);
                setLinkItem(newLinkItem);
                setErrors();
                newLinkItem.dirty = true;
                if (triggerSave) {
                    handleSave();
                }
            }
        },
        [linkItem, handleSave, debounce, setIsDirty, setErrors],
    );

    const handleDeleteClick = () => {
        onDelete(originalLinkItem);
    };

    const handleEmojiSelected = (emoji: string) => {
        handleValueChange(emoji.trim(), LinkItemType.emoji);
    };

    const onUrlFocusSet = (focused) => {
        setUrlFocused(focused);
    };

    const displayURL = useMemo(() => {
        if (editable) {
            return url;
        } else {
            return linkItem.editUrl.replace(/^https?:\/\/(www\.)?/, '');
        }
    }, [editable, url, linkItem.editUrl]);

    useEffect(() => {
        const handleScroll = () => {
            // 100 for the topbar height, only saving if they've scrolled up to the button
            if (editable && window.pageYOffset < 100 && isMobileDevice) {
                handleSave();
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [handleSave, editable]);

    const href = useMemo(() => {
        if (editable || !features.yatPageLinkTracking) {
            return linkItem.url;
        }

        return `${config.apiUrl}/redirect?emoji_id=${encodeURIComponent(emojiId)}&link=${btoa(linkItem.url)}`;
    }, [emojiId, linkItem, editable, features.yatPageLinkTracking]);

    return (
        <div
            className={`yat-link ${editable ? 'yat-link--editable' : 'yat-link--clickable'} ${
                isLastLink && 'yat-link--last'
            } ${isDragging && 'yat-link--dragging'} `}
        >
            <div onBlur={() => handleSave()} className="yat-link__content">
                <YatLinkForm isLink={!editable} href={href}>
                    <div className="yat-link__link-icon">
                        <YatLinkIcon
                            linkEmoji={linkItem.emoji}
                            editable={editable}
                            onEmojiSelect={handleEmojiSelected}
                            debounce={debounce}
                            onBlur={() => handleSave()}
                            onPickerOpened={(open) => onPickerOpened(open)}
                        />
                    </div>

                    <div className="yat-link__items">
                        <YatLinkInput
                            placeholder={t('link_title')}
                            value={linkItem.title}
                            variant={'title'}
                            disabled={!editable}
                            maxLength={25}
                            maxRows={2}
                            isInvalid={!linkItem.dataFieldsEmpty() && titleInvalid === true}
                            debounce={debounce}
                            onChange={(e) => handleValueChange(e.target.value, LinkItemType.title)}
                        />
                        {(editable || linkItem.description) && (
                            <YatLinkInput
                                placeholder={t('add_description')}
                                value={linkItem.description}
                                variant={'desc'}
                                disabled={!editable}
                                debounce={debounce}
                                maxRows={2}
                                maxLength={100}
                                onChange={(e) => handleValueChange(e.target.value, LinkItemType.description)}
                            />
                        )}
                        <YatLinkInput
                            placeholder={urlFocused ? t('paste_url') : t('link_url')}
                            setUrlFocused={(urlFocused) => onUrlFocusSet(urlFocused)}
                            value={editable ? url : displayURL}
                            debounce={debounce}
                            variant={'url'}
                            maxRows={3}
                            maxLength={2048}
                            disabled={!editable}
                            isInvalid={!linkItem.dataFieldsEmpty() && urlInvalid === true}
                            onChange={(e) => {
                                handleUrlChange(e.target.value);
                            }}
                        />
                    </div>
                    {editable && (
                        <div className="yat-link__controls">
                            {linkItem && linkItem.hash && (
                                <LegacyDragIcon className="yat-link__icon yat-link__icon--drag" />
                            )}
                            <LegacyTrashIcon className="yat-link__trash-icon" onClick={() => handleDeleteClick()} />
                        </div>
                    )}
                </YatLinkForm>
            </div>
        </div>
    );
}

export default YatLink;
